import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { jwtDecode } from "jwt-decode";
import { ReactNode, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { AuthContext, TENANT_ID_STORAGE_KEY } from "./AuthContext";

import { ROUTES } from "@/constants/routes";
import { useLogout } from "@/features/auth/api/useLogout";
import { getContextType } from "@/lib/authorization";
import { instance } from "@/lib/axios/instance";
import { useLocalStorage } from "@/lib/hooks/useLocalStorage";
import { ForgotPasswordRequest } from "@/types/request/ForgotPasswordRequest";
import { LoginRequest } from "@/types/request/LoginRequest";
import { ResetPasswordRequest } from "@/types/request/ResetPasswordRequest";
import { ForgotPasswordErrorResponse } from "@/types/response/ForgotPasswordResponse";
import {
  LoginErrorResponse,
  LoginResponse,
  TokenPayload,
} from "@/types/response/LoginResponses";
import { ResetPasswordErrorResponse } from "@/types/response/ResetPasswordResponse";
import { UserRole } from "@/types/user/userType";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const logout = useLogout();
  const [tenantId, setTenantId] = useLocalStorage(TENANT_ID_STORAGE_KEY, "");
  const [userId, setUserId] = useLocalStorage("userId", "");
  const [accessToken, updateAccessToken] = useLocalStorage("accessToken", "");
  const [role, setRole] = useLocalStorage<UserRole | null>("role", null);

  const loginMutation: UseMutationResult<
    AxiosResponse<LoginResponse>,
    AxiosError<LoginErrorResponse>,
    LoginRequest
  > = useMutation({
    mutationFn: (loginRequest: LoginRequest) =>
      instance.post<LoginResponse>("/api/v2/Auth/login", loginRequest),
    onSuccess: ({
      data: { accessToken, refreshToken },
    }: AxiosResponse<LoginResponse>) => {
      if (!accessToken || !refreshToken) {
        throw new Error("Invalid JWT token");
      }
      const decodedToken: TokenPayload = jwtDecode(accessToken);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp && decodedToken.exp < currentTime) {
        throw new Error("Token expired");
      }
      updateAccessToken(accessToken);
      setRole((decodedToken.role as UserRole) || null);
      setUserId(decodedToken.nameid || "");

      localStorage.setItem("email", decodedToken.email || "");
      localStorage.setItem("firstname", decodedToken.firstName || "");
      localStorage.setItem("infix", decodedToken.infix || "");
      localStorage.setItem("lastname", decodedToken.lastName || "");
      localStorage.setItem("refreshToken", refreshToken);
      const context = getContextType({
        clientId: decodedToken.clientId,
      });

      if (context === "admin") {
        navigate(`/${ROUTES.clients}`, { replace: true });
        return;
      }
      // context is "client"
      navigate(`/${ROUTES.home}`, { replace: true });
    },
  });

  const forgotPasswordMutation: UseMutationResult<
    AxiosResponse,
    AxiosError<ForgotPasswordErrorResponse>,
    ForgotPasswordRequest
  > = useMutation({
    mutationFn: (resetPasswordRequest: ForgotPasswordRequest) =>
      instance.post("/api/v2/Auth/forgot-password", resetPasswordRequest),
    onSuccess: () => {
      navigate(ROUTES.linkSent, { replace: true });
    },
  });

  const resetPasswordMutation: UseMutationResult<
    AxiosResponse,
    AxiosError<ResetPasswordErrorResponse>,
    ResetPasswordRequest
  > = useMutation({
    mutationFn: (resetPasswordRequest: ResetPasswordRequest) =>
      instance.post("/api/v2/Auth/reset-password", resetPasswordRequest),
    onSuccess: () => {
      navigate(ROUTES.login, { replace: true, state: { passwordReset: true } });
    },
  });

  const decodedToken = useMemo(() => {
    if (!accessToken) {
      return null;
    }

    try {
      return jwtDecode<TokenPayload>(accessToken);
    } catch (e) {
      console.warn("Failed to decode access token", e);
      return null;
    }
  }, [accessToken]);

  const getContext = useMemo(
    () => (decodedToken ? getContextType({ clientId: decodedToken.clientId }) : null),
    [decodedToken],
  );

  const firstName = decodedToken?.firstName ?? null;
  const clientId = decodedToken?.clientId || null;

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        clientId,
        context: getContext,
        firstName,
        forgetPasswordLoading: forgotPasswordMutation.isPending,
        forgotPassword: forgotPasswordMutation.mutate,
        forgotPasswordError:
          forgotPasswordMutation.failureReason?.response?.data,
        loading: loginMutation.isPending,
        login: loginMutation.mutate,
        loginError: loginMutation.failureReason?.response
          ? {
            status: loginMutation.failureReason.response.status,
            data: loginMutation.failureReason.response.data,
          }
          : undefined,
        logout,
        resetForgotPasswordError: forgotPasswordMutation.reset,
        resetLoginUserError: loginMutation.reset,
        resetPassword: resetPasswordMutation.mutate,
        resetPasswordError: resetPasswordMutation.failureReason?.response
          ? {
            status: resetPasswordMutation.failureReason.response.status,
            data: resetPasswordMutation.failureReason.response.data,
          }
          : undefined,
        resetPasswordLoading: resetPasswordMutation.isPending,
        resetResetPasswordError: resetPasswordMutation.reset,
        role,
        setTenantId,
        tenantId,
        userId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
